/**
 * Phase 3b — Deal team chip picker.
 *
 * Renders `deal_team_emails` (a list of @ember-infra.com addresses)
 * as removable chips. Click "+ add" to open a roster dropdown with
 * typeahead filter; pick a name → chip appears.
 *
 * Display-only (`onChange={undefined}`) renders the chips without
 * the add/remove affordances — used when the field isn't in edit
 * mode.
 *
 * Per spec §8: roster filtered to `team_classification='investment'`
 * by default; non-roster emails are NOT accepted (the picker is
 * the only path).
 */
import { useMemo, useState } from 'react';

import { type TeamRosterMember, useTeamRoster } from '../../hooks/useTeamRoster';
import styles from './DealsPage.module.css';

interface Props {
  value: string[];
  onChange?: (next: string[]) => void;
  disabled?: boolean;
}

export function TeamChipPicker({ value, onChange, disabled }: Props) {
  const rosterQuery = useTeamRoster('investment');
  const roster = rosterQuery.data?.members ?? [];

  const byEmail = useMemo(() => {
    const m = new Map<string, TeamRosterMember>();
    for (const r of roster) {
      if (r.email) m.set(r.email.toLowerCase(), r);
    }
    return m;
  }, [roster]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const selected = new Set(value.map((v) => v.toLowerCase()));
    return roster.filter((r) => {
      if (!r.email) return false;
      if (selected.has(r.email.toLowerCase())) return false;
      if (!f) return true;
      return (
        (r.name || '').toLowerCase().includes(f)
        || (r.short_name || '').toLowerCase().includes(f)
        || r.email.toLowerCase().includes(f)
      );
    });
  }, [roster, filter, value]);

  const editable = !!onChange && !disabled;

  const add = (email: string) => {
    if (!onChange) return;
    const lower = email.toLowerCase();
    if (value.some((v) => v.toLowerCase() === lower)) return;
    onChange([...value, email]);
    setFilter('');
    setPickerOpen(false);
  };

  const remove = (email: string) => {
    if (!onChange) return;
    onChange(value.filter((v) => v.toLowerCase() !== email.toLowerCase()));
  };

  return (
    <div className={styles.teamChipPicker}>
      <div className={styles.teamChipList}>
        {value.length === 0 && (
          <span className={styles.teamChipEmpty}>(no team assigned)</span>
        )}
        {value.map((email) => {
          const member = byEmail.get(email.toLowerCase());
          const display = member?.short_name || member?.name || email;
          return (
            <span key={email} className={styles.teamChip}>
              {display}
              {editable && (
                <button
                  type="button"
                  className={styles.teamChipRemove}
                  onClick={() => remove(email)}
                  title={`Remove ${display}`}
                >
                  x
                </button>
              )}
            </span>
          );
        })}
        {editable && (
          <button
            type="button"
            className={styles.teamChipAddBtn}
            onClick={() => setPickerOpen(!pickerOpen)}
            disabled={rosterQuery.isLoading}
          >
            + add
          </button>
        )}
      </div>
      {editable && pickerOpen && (
        <div className={styles.teamChipPicker_dropdown}>
          <input
            type="text"
            className={styles.teamChipPicker_filter}
            placeholder="filter by name or email"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
          />
          <div className={styles.teamChipPicker_list}>
            {filtered.length === 0 && (
              <div className={styles.teamChipPicker_empty}>
                {rosterQuery.isLoading ? 'Loading roster...' : 'No matches.'}
              </div>
            )}
            {filtered.map((r) => (
              <button
                key={r.email}
                type="button"
                className={styles.teamChipPicker_option}
                onClick={() => add(r.email)}
              >
                <span className={styles.teamChipPicker_name}>
                  {r.name || r.email}
                </span>
                {r.title && (
                  <span className={styles.teamChipPicker_title}>
                    {r.title}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
